import { useParams } from 'react-router';
import {
    RequestStatus,
    useServerErrorEffect,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import {
    ApprovingContractInfo,
    BarrierGateIdentificationEntity,
    OffstreetTransactionActions,
    PassageInfo,
} from './OperatorOffstreetTransactionTypes.ts';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { useEffect, useState } from 'react';
import { OperatorRoutedModalContent } from '../layout/components/OperatorRoutedModalContent.tsx';
import { HeaderComponent } from '../layout/components/HeaderComponent.tsx';
import { Localized, useLocalize } from 'dg-web-shared/lib/Localization.tsx';
import { HeaderActionCloseButtons } from '../layout/components/HeaderActionCloseButtons.tsx';
import {
    ApprovingContractInfoField,
    BarrierGateIdentificationEnityValue,
    getNetPayedPriceString,
    NoReceiptForContractWithoutPayments,
    OffstreetTransactionActionText,
    OffstreetTransactionOpen,
    OffstreetTransactionPassageText,
    OffstreetTransactionRefundBlocked,
    OffstreetTransactionTransit,
    OperatorOffstreetTransactionDetailRoutedModal,
} from './OperatorOffstreetTransactionShared.tsx';
import { HeaderCancelSaveButtons } from '../layout/components/HeaderCancelSaveButtons.tsx';
import { Box, Stack } from '@mui/material';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';
import { TerminalProvider } from '../common/state/useParkingsFetch.ts';
import { PaymentChannel } from '../common/models/PaymentChannel.ts';
import { ReadOnlyTextField } from '../ui/material-ui/ReadOnlyTextField.tsx';
import {
    BlockLabel,
    FormContentLayout,
    FormLeftColumn,
    FormRightColumn,
} from '../layout/components/form/FormLayout.tsx';
import { openNewTab } from 'dg-web-shared/common/utils/Navigation.tsx';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import {
    OffstreetTransactionPayStationPayments,
    PaystationPaymentData,
} from './OperatorOffstreetTransactionTicket.tsx';
import { Message } from 'dg-web-shared/lib/Localized.ts';
import { PaymentChannelLocalized } from '../common/components/PaymentChannelLocalized.tsx';
import {
    isInputAmountRefundable,
    OperatorRefund,
    parseChf,
    TransactionRefundBlockedExplanation,
    TransactionRefundInputValues,
    TransactionRefundPayload,
} from '../common/models/OperatorRefund.ts';
import {
    TransactionRefundInputFields,
    TransactionRefundResponseBanner,
} from '../common/components/TransactionRefund.tsx';
import { urlWithOperatorExternalId } from '../utils/Utils.ts';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';

export interface OffstreetTransactionContractDetails {
    barrierGateIdentificationEntity: BarrierGateIdentificationEntity;
    barrierGateProvider: TerminalProvider | null;
    refundBlockedExplanation: TransactionRefundBlockedExplanation | null;
    zoneName: string;
    entryInfo: PassageInfo;
    transitsInfo: PassageInfo[] | null;
    exitInfo: PassageInfo;
    contractExternalId: string;
    paymentChannel: PaymentChannel;
    createdAt: string;
    duration: string;
    priceChf: number;
    refund: OperatorRefund | null;
    approvingContractInfo: ApprovingContractInfo | null;
    bprofile: number | null;
    coopRebate: CoopRebate | null;
    paystationPayments: PaystationPaymentData[] | null;
    contractRecodes: ContractRecodeInfo[];
}

export interface ContractRecodeInfo {
    recodeConditionDescription: Message;
    recodeDiscount: string;
}

interface CoopRebate {
    rebatePercentage: number | null;
    rebateStringAmount: string | null;
}

export function OperatorOffstreetTransactionContract() {
    const { contractExternalId } = useParams<{ contractExternalId: string }>();
    const [offstreetTransactionsState, refetchOffstreetTransaction] =
        useServerFetch<
            OffstreetTransactionContractDetails,
            { contractExternalId: string },
            null
        >(
            context => ({
                url: `/ui-api/operator-account/offstreet-transactions/contract/${context.contractExternalId}`,
            }),
            contractExternalId ? { contractExternalId } : null,
        );

    return (
        <OperatorOffstreetTransactionDetailRoutedModal
            render={controller => (
                <OperatorAsyncLoadedSection
                    requestState={offstreetTransactionsState}
                    render={transaction => (
                        <TransactionTicketContractDetailContent
                            transaction={transaction}
                            onClose={controller.close}
                            refetchTransaction={refetchOffstreetTransaction}
                            onDirtyStateChange={
                                controller.setShouldConfirmBeforeLeave
                            }
                        />
                    )}
                />
            )}
        />
    );
}

function TransactionTicketContractDetailContent({
    transaction,
    onClose,
    refetchTransaction,
    onDirtyStateChange,
}: {
    transaction: OffstreetTransactionContractDetails;
    onClose: () => void;
    refetchTransaction: () => void;
    onDirtyStateChange: (shouldConfirm: boolean) => void;
}) {
    const localize = useLocalize();
    const { operatorExternalId } = useOperatorContext();
    const [openRefundResponseBanner, setOpenRefundResponseBanner] =
        useState(false);
    const [refundInputVisible, setRefundInputVisible] = useState(false);
    const [refundInputValues, setRefundInputValues] =
        useState<TransactionRefundInputValues>({
            amount: '',
            remark: '',
        });
    const [refundTransactionState, refundTransaction] = useServerWrite<
        TransactionRefundPayload,
        null
    >(({ contractExternalId }) => ({
        url: `/ui-api/operator-account/park-transaction/${contractExternalId}/refund`,
    }));

    function onRefundTransactionStateChange() {
        setOpenRefundResponseBanner(true);
        setRefundInputValues({
            amount: '',
            remark: '',
        });
        setRefundInputVisible(false);
    }

    useServerSuccessEffect(refundTransactionState, () => {
        refetchTransaction();
        onRefundTransactionStateChange();
    });

    useServerErrorEffect(
        refundTransactionState,
        onRefundTransactionStateChange,
    );

    useEffect(() => {
        onDirtyStateChange(refundInputVisible);
    }, [onDirtyStateChange, refundInputVisible]);

    const amountRappen = Math.round(parseChf(refundInputValues.amount) * 100);
    const isRefundable = isInputAmountRefundable(
        Math.round(transaction.priceChf * 100),
        amountRappen,
    );

    const refundAmountChf = transaction.refund
        ? transaction.refund.refundAmountRappen / 100
        : null;
    const netPayedPriceChf = getNetPayedPriceString(
        transaction.priceChf,
        refundAmountChf,
    );

    const hasNoReceipt =
        transaction.paymentChannel === PaymentChannel.PAY_STATION &&
        transaction.paystationPayments !== null &&
        transaction.paystationPayments.length === 0;

    return (
        <OperatorRoutedModalContent
            header={
                <HeaderComponent
                    title={
                        <Localized
                            de="Parkvorgang"
                            fr="Procédure"
                            it="Procedura"
                            en="Procedure"
                        />
                    }
                    editMode={refundInputVisible}
                    buttons={
                        !refundInputVisible ? (
                            <HeaderActionCloseButtons
                                onClose={onClose}
                                dropdownItems={[
                                    {
                                        key: 'reimburse',
                                        label: (
                                            <OffstreetTransactionActionText
                                                action={
                                                    OffstreetTransactionActions.REIMBURSE_CUSTOMER
                                                }
                                            />
                                        ),
                                        onClickCallback: () =>
                                            setRefundInputVisible(true),
                                        disabled:
                                            transaction.refundBlockedExplanation !==
                                            null,
                                        tooltip:
                                            transaction.refundBlockedExplanation ? (
                                                <OffstreetTransactionRefundBlocked
                                                    refundBlockedExplanation={
                                                        transaction.refundBlockedExplanation
                                                    }
                                                />
                                            ) : undefined,
                                    },
                                    {
                                        key: 'download-receipt',
                                        label: (
                                            <OffstreetTransactionActionText
                                                action={
                                                    OffstreetTransactionActions.DOWNLOAD_RECEIPT
                                                }
                                            />
                                        ),
                                        onClickCallback: () => {
                                            openNewTab(
                                                urlWithOperatorExternalId(
                                                    `/ui-api/operator-account/park-transaction/${transaction.contractExternalId}/receipt`,
                                                    operatorExternalId,
                                                ),
                                            );
                                        },
                                        disabled: hasNoReceipt,
                                        tooltip: hasNoReceipt ? (
                                            <NoReceiptForContractWithoutPayments />
                                        ) : undefined,
                                    },
                                ]}
                            />
                        ) : (
                            <HeaderCancelSaveButtons
                                onCancel={() => {
                                    setRefundInputValues({
                                        amount: '',
                                        remark: '',
                                    });
                                    setRefundInputVisible(false);
                                }}
                                onSave={() => {
                                    if (isRefundable) {
                                        refundTransaction({
                                            contractExternalId:
                                                transaction.contractExternalId,
                                            amountRappen: amountRappen,
                                            remark: refundInputValues.remark,
                                        });
                                    }
                                }}
                                saveDisabled={!isRefundable}
                                loading={
                                    refundTransactionState.status ===
                                    RequestStatus.PENDING
                                }
                            />
                        )
                    }
                />
            }
            body={
                <FormContentLayout>
                    <FormLeftColumn>
                        <OffstreetTransactionOpen
                            isPresent={false}
                            hasAntipassback={false}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Identifikation"
                                    fr="Identification"
                                    it="Identificazione"
                                    en="Identification"
                                />
                            }
                            value={
                                <BarrierGateIdentificationEnityValue
                                    barrierGateIdentificationEntity={
                                        transaction.barrierGateIdentificationEntity
                                    }
                                />
                            }
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Einfahrt"
                                    fr="Entrée"
                                    it="Entrata"
                                    en="Entry"
                                />
                            }
                            value={
                                <OffstreetTransactionPassageText
                                    passageInfo={transaction.entryInfo}
                                />
                            }
                        />
                        <OffstreetTransactionTransit
                            transitsInfo={transaction.transitsInfo}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Ausfahrt"
                                    fr="Sortie"
                                    it="Uscita"
                                    en="Exit"
                                />
                            }
                            value={
                                <OffstreetTransactionPassageText
                                    passageInfo={transaction.exitInfo}
                                />
                            }
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Dauer"
                                    fr="Durée"
                                    it="Durata"
                                    en="Duration"
                                />
                            }
                            value={transaction.duration}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Parking"
                                    fr="Parking"
                                    it="Parcheggio"
                                    en="Parking"
                                />
                            }
                            value={transaction.zoneName}
                        />
                    </FormLeftColumn>
                    <FormRightColumn>
                        <TransactionRefundResponseBanner
                            open={openRefundResponseBanner}
                            onClose={() => setOpenRefundResponseBanner(false)}
                            success={
                                refundTransactionState.status ===
                                RequestStatus.SUCCESS
                            }
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Bezahlt"
                                    fr="Payé"
                                    it="Pagato"
                                    en="Paid"
                                />
                            }
                            value={netPayedPriceChf}
                        />
                        {refundInputVisible && (
                            <TransactionRefundInputFields
                                price={transaction.priceChf}
                                refundInputValues={refundInputValues}
                                setRefundInputValues={setRefundInputValues}
                            />
                        )}
                        {transaction.contractRecodes.length > 0 && (
                            <Stack
                                direction={'row'}
                                sx={{
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box sx={{ display: 'flex', flexGrow: 1 }}>
                                    <ReadOnlyTextField
                                        label={
                                            <Localized
                                                de="Rabatt"
                                                fr="Rabais"
                                                it="Sconto"
                                                en="Rebate"
                                            />
                                        }
                                        value={
                                            <Box
                                                sx={{
                                                    whiteSpace: 'pre-line',
                                                }}
                                            >
                                                {transaction.contractRecodes
                                                    .map(
                                                        contractRecode =>
                                                            contractRecode.recodeDiscount,
                                                    )
                                                    .join('\n')}
                                            </Box>
                                        }
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', flexGrow: 1 }}>
                                    <ReadOnlyTextField
                                        label={
                                            <Localized
                                                de="Bedingung"
                                                fr="Condition"
                                                it="Condizione"
                                                en="Condition"
                                            />
                                        }
                                        value={
                                            <Box
                                                sx={{
                                                    whiteSpace: 'pre-line',
                                                }}
                                            >
                                                {transaction.contractRecodes
                                                    .map(contractRecode =>
                                                        localize(
                                                            contractRecode.recodeConditionDescription,
                                                        ),
                                                    )
                                                    .join('\n')}
                                            </Box>
                                        }
                                    />
                                </Box>
                            </Stack>
                        )}
                        {transaction.coopRebate && (
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Coop Rabatt"
                                        fr="Rabais Coop"
                                        it="Sconto Coop"
                                        en="Coop discount"
                                    />
                                }
                                value={
                                    transaction.coopRebate.rebateStringAmount ??
                                    (transaction.coopRebate.rebatePercentage
                                        ? `${transaction.coopRebate.rebatePercentage}%`
                                        : EmDash)
                                }
                            />
                        )}
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Kanal"
                                    fr="Canal"
                                    it="Canale"
                                    en="Channel"
                                />
                            }
                            value={
                                <PaymentChannelLocalized
                                    paymentChannel={transaction.paymentChannel}
                                />
                            }
                        />
                        {transaction.paystationPayments && (
                            <OffstreetTransactionPayStationPayments
                                paystationPayments={
                                    transaction.paystationPayments
                                }
                            />
                        )}
                        {transaction.refund && (
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Rückzahlung"
                                        fr="Remboursement"
                                        it="Rimborso"
                                        en="Refund"
                                    />
                                }
                                value={
                                    <>
                                        {`CHF ${(transaction.refund.refundAmountRappen / 100).toFixed(2)}`}
                                        <br />
                                        {Formatter.dayMonthYearHourMinute(
                                            DateTime.fromISO(
                                                transaction.refund.refundedAt,
                                            ),
                                        )}
                                        <br />
                                        {transaction.refund.remark}
                                        <br />
                                        {transaction.refund.refundedBy}
                                    </>
                                }
                            />
                        )}
                        <ApprovingContractInfoField
                            approvingContractInfo={
                                transaction.approvingContractInfo
                            }
                            bProfile={transaction.bprofile}
                        />
                        <Box
                            sx={{
                                flexGrow: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'end',
                            }}
                        >
                            <BlockLabel
                                label={
                                    <Localized
                                        de="Erstellt am"
                                        fr="Créé le"
                                        it="Creato il"
                                        en="Created at"
                                    />
                                }
                            >
                                {Formatter.dayMonthYearHourMinute(
                                    DateTime.fromISO(transaction.createdAt),
                                )}
                            </BlockLabel>
                            <BlockLabel label="ID">
                                {transaction.contractExternalId}
                            </BlockLabel>
                        </Box>
                    </FormRightColumn>
                </FormContentLayout>
            }
        />
    );
}
